// Small utilities shared by every Unaffiliated page. Kept out of
// pages/UnaffiliatedGrid.jsx so the profile, the video log, and the
// admin Graduations page can all reuse the exact same formatters.

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Turn a YYYY-MM-DD (or any Date-parseable string) into "September 27, 2026".
// Falls back to a dash when the input can't be parsed — never returns raw
// ISO on the UI so column widths stay predictable.
export function fmtDateWords(iso) {
  if (!iso) return "—";
  const s = String(iso).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) {
    const y = m[1];
    const mi = Number(m[2]);
    const d = Number(m[3]);
    if (mi >= 1 && mi <= 12 && d >= 1 && d <= 31) {
      return `${MONTHS[mi - 1]} ${d}, ${y}`;
    }
  }
  const dt = new Date(s);
  if (!Number.isNaN(dt.getTime())) {
    return `${MONTHS[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  }
  return "—";
}

// Ordered list of audience buckets — kept in sync with the backend
// `_AUDIENCE_BUCKETS` order. Consolidated 2026-09-29 to two buckets
// after diagnostics (Stocks/Learning/Other never fired against the
// live sheet vocabulary; their anchors folded into Index & TradFi).
export const AUDIENCE_BUCKETS = [
  "Index & TradFi",
  "Commodities",
];

// v36: per-list bucket catalogs. The MegaDump sheet uses "Index &
// TradFi" / "Commodities"; the Crypto sheet uses "Crypto" / "Forex" /
// "TradFi"; the combined "all" view surfaces every bucket a user
// might see so one filter dropdown fits all three lists. Each entry
// is `{ label, value }` — `label` is what the user sees, `value` is
// what the backend filter receives (comma-separated when the same
// user-facing label maps to multiple backend buckets, e.g. "TradFi"
// in the all-list spans both "TradFi" (crypto sheet) and "Index &
// TradFi" (megadump sheet)).
export const AUDIENCE_BUCKETS_BY_LIST = {
  megadump: [
    { label: "TradFi",      value: "Index & TradFi" },
    { label: "Commodities", value: "Commodities" },
    { label: "Crypto",      value: "Crypto" },
  ],
  crypto: [
    { label: "Crypto", value: "Crypto" },
    { label: "Forex",  value: "Forex" },
    { label: "TradFi", value: "TradFi" },
  ],
  all: [
    { label: "Crypto",      value: "Crypto" },
    { label: "Forex",       value: "Forex" },
    { label: "TradFi",      value: "TradFi,Index & TradFi" },
    { label: "Commodities", value: "Commodities" },
  ],
};

// Round-5: display-only mapping — the sheet value / backend key for
// the "stocks + indices + tradfi" bucket is still "Index & TradFi"
// (the Google Sheet writes it, every /unaffiliated/* endpoint returns
// it, filter payloads carry it). Only the user-visible label changes
// to "TradFi" so the Unaffiliated side reads the same way as the
// promoter side after the Round-4 Nifty→TradFi sweep. Pass any
// bucket name through this helper before rendering — unknown keys
// (including "Commodities") fall through unchanged.
export const AUDIENCE_DISPLAY_LABELS = {
  "Index & TradFi": "TradFi",
};

export function audienceDisplayLabel(key) {
  return AUDIENCE_DISPLAY_LABELS[key] || key || "";
}

// v36: Crypto gets its own accent (amber/orange) so the chip reads
// cleanly against the dark UI and is unmistakeable from the TradFi
// cyan. "TradFi" (crypto-sheet bucket) and "Index & TradFi"
// (megadump bucket) share the cyan so both display as "TradFi" with
// one colour across every list. Forex gets a violet accent so the
// three-bucket crypto donut (Crypto / Forex / TradFi) reads as three
// distinct slices.
export const AUDIENCE_COLORS = {
  "Index & TradFi":       "#22D3EE",
  "TradFi":               "#22D3EE",
  "Commodities":          "#F472B6",
  "Crypto":               "#F59E0B",
  "Forex":                "#A78BFA",
};

// Dominant upload style from a creator row's live/shorts/normal counts.
// Returns "live" | "shorts" | "normal" | "any" (matches backend).
export function dominantUploadStyle(row) {
  const l = row?.live_videos || 0;
  const s = row?.shorts || 0;
  const n = row?.normal_videos || 0;
  const m = Math.max(l, s, n);
  if (m === 0) return "any";
  if (m === l) return "live";
  if (m === s) return "shorts";
  return "normal";
}

export const UPLOAD_LABELS = {
  live:   "Live",
  shorts: "Shorts",
  normal: "Normal",
  any:    "Mixed",
};
